// ---------------------------------------------------------------------------
// Alert banner data model (frontend DTOs).
//
// Mirrors the API contract exposed by `AlertsController` and the
// `[Surface].[Alerts]` table (see backend `PartnerProgramApi.Models.Alert`).
// ---------------------------------------------------------------------------

export type AlertSeverity = 'Info' | 'Success' | 'Warning' | 'Critical';

export type AlertMessageKey =
  | 'ServiceOutage'
  | 'ServiceDegradation'
  | 'ScheduledMaintenance'
  | 'DataQualityIssue'
  | 'FeatureTemporarilyUnavailable'
  | 'DataRefreshDelay'
  | 'IssueResolved'
  | 'MaintenanceComplete'
  | 'NewFeatureAvailable';

export interface Alert {
  id: string;
  messageKey: AlertMessageKey;
  parameters: Record<string, string> | null;
  severity: AlertSeverity;
  isDismissible: boolean;
  startTimeUtc: string;
  endTimeUtc: string;
  targetTiers: string[] | null;
  targetLocationIds: string[] | null;
  createdBy: string;
  createdAt: string;
}

export interface AlertsResponse {
  alerts: Alert[];
}

/** Derived scheduling state, computed client-side from start/end times. */
export type AlertStatus = 'Active' | 'Scheduled' | 'Inactive';

/**
 * Derives the {@link AlertStatus} of an alert at a given point in time
 * (defaults to now) by comparing the current instant to its scheduling window.
 */
export const getAlertStatus = (alert: Alert, now: number = Date.now()): AlertStatus => {
  const start = new Date(alert.startTimeUtc).getTime();
  const end = new Date(alert.endTimeUtc).getTime();
  if (Number.isFinite(start) && now < start) return 'Scheduled';
  if (Number.isFinite(end) && now > end) return 'Inactive';
  return 'Active';
};
